@if (compact) {
  <!-- Cheat sheet: vertical, fixed width -->
  <div class="flex flex-col flex-wrap items-center gap-2">
    <div
      #shortcutInput
      [id]="inputId"
      tabindex="0"
      (keydown)="onKeyDown($event)"
      (focus)="onFocus()"
      (blur)="onBlur()"
      class="flex flex-wrap items-center gap-2 min-h-[2.5rem] w-72 min-w-72 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
      [attr.data-testid]="'shortcut-record-input'"
    >
      @for (combo of combosInput(); track combo; let i = $index) {
        <span class="inline-flex items-center gap-1">
          <app-shortcut-badge [combo]="combo"></app-shortcut-badge>
          <button
            type="button"
            (click)="removeCombo(i)"
            class="p-0.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
            [attr.aria-label]="'common.remove' | translate"
          >
            <i class="fa fa-times text-xs"></i>
          </button>
        </span>
      }
      @if (comboPreview()) {
        <span class="inline-flex items-center gap-1">
          <app-shortcut-badge [combo]="comboPreview()" [highlight]="true"></app-shortcut-badge>
          <button
            type="button"
            (click)="clearPreview()"
            class="p-0.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
            [attr.aria-label]="'common.remove' | translate"
          >
            <i class="fa fa-times text-xs"></i>
          </button>
          @if (!conflictError()) {
            <button
              type="button"
              (click)="addCurrentCombo()"
              class="p-0.5 text-green-500 hover:text-green-600 dark:text-green-400 dark:hover:text-green-300 rounded focus:outline-none focus:ring-1 focus:ring-green-400"
              [attr.aria-label]="'hotkeys.shortcut-confirm-enter' | translate"
            >
              <i class="fa fa-check text-xs"></i>
            </button>
          }
        </span>
      }
      @if (combosInput().length === 0 && !comboPreview() && !isFocused()) {
        <span class="text-gray-400 dark:text-gray-500 text-sm">{{ 'hotkeys.shortcut-placeholder' | translate }}</span>
      }
      @if (isFocused() && !comboPreview()) {
        <span class="text-gray-400 dark:text-gray-500 text-sm">{{ 'hotkeys.shortcut-now-typing' | translate }}</span>
      }
    </div>
    <div class="w-full">
      @if (validationError(); as err) {
        <p class="text-red-500 text-sm m-0">{{ err | translate }}</p>
      }
      @if (conflictError(); as err) {
        <p class="text-amber-600 dark:text-amber-400 text-sm m-0 flex items-center gap-2 flex-wrap">
          <span>{{ err | translate }}</span>
          @if (canOverwriteConflict()) {
            <button
              type="button"
              (click)="addCurrentComboWithOverwrite()"
              class="text-xs px-2 py-0.5 rounded border border-amber-500 dark:border-amber-400 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30 focus:outline-none focus:ring-1 focus:ring-amber-500"
              [attr.aria-label]="'hotkeys.shortcut-overwrite' | translate"
              [title]="'hotkeys.shortcut-overwrite' | translate"
            >
              <i class="fa fa-pen-to-square text-xs"></i>
            </button>
          }
        </p>
      }
      @if (reservedHint(); as hint) {
        <p class="text-amber-600 dark:text-amber-400 text-sm m-0">{{ hint | translate }}</p>
      }
    </div>
  </div>
} @else {
  <!-- Dialog: input only (Enter to add, Save auto-adds) -->
  <div class="flex flex-wrap items-center gap-2">
    <div
      #shortcutInput
      [id]="inputId"
      tabindex="0"
      (keydown)="onKeyDown($event)"
      (focus)="onFocus()"
      (blur)="onBlur()"
      class="flex flex-wrap items-center gap-2 min-h-[2.5rem] flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
      [attr.data-testid]="'shortcut-record-input'"
    >
      @for (combo of combosInput(); track combo; let i = $index) {
        <span class="inline-flex items-center gap-1">
          <app-shortcut-badge [combo]="combo"></app-shortcut-badge>
          <button
            type="button"
            (click)="removeCombo(i)"
            class="p-0.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
            [attr.aria-label]="'common.remove' | translate"
          >
            <i class="fa fa-times text-xs"></i>
          </button>
        </span>
      }
      @if (comboPreview()) {
        <span class="inline-flex items-center gap-1">
          <app-shortcut-badge [combo]="comboPreview()" [highlight]="true"></app-shortcut-badge>
          <button
            type="button"
            (click)="clearPreview()"
            class="p-0.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
            [attr.aria-label]="'common.remove' | translate"
          >
            <i class="fa fa-times text-xs"></i>
          </button>
          @if (!conflictError()) {
            <button
              type="button"
              (click)="addCurrentCombo()"
              class="p-0.5 text-green-500 hover:text-green-600 dark:text-green-400 dark:hover:text-green-300 rounded focus:outline-none focus:ring-1 focus:ring-green-400"
              [attr.aria-label]="'hotkeys.shortcut-confirm-enter' | translate"
            >
              <i class="fa fa-check text-xs"></i>
            </button>
          }
        </span>
      }
      @if (combosInput().length === 0 && !comboPreview() && !isFocused()) {
        <span class="text-gray-400 dark:text-gray-500 text-sm">{{ 'hotkeys.shortcut-placeholder' | translate }}</span>
      }
      @if (isFocused() && !comboPreview()) {
        <span class="text-gray-400 dark:text-gray-500 text-sm">{{ 'hotkeys.shortcut-now-typing' | translate }}</span>
      }
    </div>
    <div class="w-full">
      @if (validationError(); as err) {
        <p class="text-red-500 text-sm m-0">{{ err | translate }}</p>
      }
      @if (conflictError(); as err) {
        <p class="text-amber-600 dark:text-amber-400 text-sm m-0 flex items-center gap-2 flex-wrap">
          <span>{{ err | translate }}</span>
          @if (canOverwriteConflict()) {
            <button
              type="button"
              (click)="addCurrentComboWithOverwrite()"
              class="text-sm px-2 py-1 rounded border border-amber-500 dark:border-amber-400 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30 focus:outline-none focus:ring-1 focus:ring-amber-500"
              [attr.aria-label]="'hotkeys.shortcut-overwrite' | translate"
            >
              {{ 'hotkeys.shortcut-overwrite' | translate }}
            </button>
          }
        </p>
      }
      @if (reservedHint(); as hint) {
        <p class="text-amber-600 dark:text-amber-400 text-sm m-0">{{ hint | translate }}</p>
      }
    </div>
  </div>
}
